/**
 * QuizPlayer - Interactive quiz component for course exams.
 * 
 * Features:
 * - Sequential question display
 * - Immediate feedback on answers
 * - Wrong answer handling with course link
 * - Success celebration on completion
 */

import { useState, useCallback } from "react";
import {
  IconCheck,
  IconX,
  IconTrophy,
  IconBookOpen,
  IconExternalLink,
  IconRefresh,
} from "../../../components/Icons.tsx";
import { getTelegram, isTelegramMiniApp } from "../../../lib/telegram.ts";
import { useSubmitAnswer } from "../hooks";
import type { CourseWithExam, ExamQuestionView } from "../hooks/useCourse";
import type { AnswerResult } from "../hooks/useSubmitAnswer";

// =============================================================================
// TYPES
// =============================================================================

interface QuizPlayerProps {
  course: CourseWithExam;
  onComplete: () => void;
  onExit: () => void;
}

type AnswerState = "idle" | "submitting" | "correct" | "wrong";

// =============================================================================
// OPTION BUTTON COMPONENT
// =============================================================================

interface OptionButtonProps {
  optionId: string;
  text: string;
  selected: boolean;
  disabled: boolean;
  answerState: AnswerState;
  selectedOptionId: string | null;
  onClick: () => void;
}

function OptionButton({
  optionId,
  text,
  selected,
  disabled,
  answerState,
  selectedOptionId,
  onClick,
}: OptionButtonProps) {
  const isSelectedOption = selectedOptionId === optionId;
  
  let bgClass = "bg-white border-slate-200 hover:border-slate-300";
  let textClass = "text-slate-900";
  let iconElement: React.ReactNode = null;

  if (answerState === "correct" && isSelectedOption) {
    bgClass = "bg-emerald-50 border-emerald-300";
    textClass = "text-emerald-900";
    iconElement = (
      <div className="w-6 h-6 rounded-full bg-emerald-500 flex items-center justify-center flex-shrink-0">
        <IconCheck className="w-4 h-4 text-white" />
      </div>
    );
  } else if (answerState === "wrong" && isSelectedOption) {
    bgClass = "bg-red-50 border-red-300";
    textClass = "text-red-900";
    iconElement = (
      <div className="w-6 h-6 rounded-full bg-red-500 flex items-center justify-center flex-shrink-0">
        <IconX className="w-4 h-4 text-white" />
      </div>
    );
  } else if (selected) {
    bgClass = "bg-blue-50 border-blue-300";
    textClass = "text-blue-900";
  }

  return (
    <button
      type="button"
      onClick={onClick}
      disabled={disabled}
      className={`w-full flex items-center gap-3 p-4 rounded-xl border-2 text-left transition-all ${bgClass} ${
        disabled ? "cursor-default" : "active:scale-[0.99]"
      }`}
    >
      <div
        className={`w-6 h-6 rounded-full border-2 flex items-center justify-center flex-shrink-0 text-xs font-bold ${
          selected && answerState === "idle"
            ? "border-blue-500 bg-blue-500 text-white"
            : answerState !== "idle" && isSelectedOption
              ? "border-transparent"
              : "border-slate-300 text-slate-500"
        }`}
      >
        {iconElement || optionId.toUpperCase()}
      </div>
      <span className={`text-sm font-medium ${textClass}`}>{text}</span>
    </button>
  );
}

// =============================================================================
// WRONG ANSWER MODAL
// =============================================================================

interface WrongAnswerModalProps {
  wrongAttemptCount: number;
  courseUrl: string | null;
  onRetry: () => void;
  onOpenCourse: () => void;
}

function WrongAnswerModal({
  wrongAttemptCount,
  courseUrl,
  onRetry,
  onOpenCourse,
}: WrongAnswerModalProps) {
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50">
      <div className="w-full max-w-sm bg-white rounded-2xl shadow-xl overflow-hidden animate-in zoom-in-95 duration-200">
        <div className="p-6 text-center">
          <div className="w-16 h-16 rounded-full bg-red-100 flex items-center justify-center mx-auto mb-4">
            <IconX className="w-8 h-8 text-red-500" />
          </div>
          <h3 className="text-lg font-bold text-slate-900 mb-2">
            Неправильный ответ
          </h3>
          <p className="text-sm text-slate-600 mb-1">
            Попробуйте ещё раз или изучите материалы курса.
          </p>
          <p className="text-xs text-slate-400">
            Попыток на этом вопросе: {wrongAttemptCount}
          </p>
        </div>
        <div className="p-4 pt-0 space-y-2">
          <button
            type="button"
            onClick={onRetry}
            className="w-full flex items-center justify-center gap-2 p-3 rounded-xl bg-blue-600 text-white font-medium transition-colors hover:bg-blue-700 active:bg-blue-800"
          >
            <IconRefresh className="w-4 h-4" />
            Попробовать ещё раз
          </button>
          {courseUrl && (
            <button
              type="button"
              onClick={onOpenCourse}
              className="w-full flex items-center justify-center gap-2 p-3 rounded-xl bg-slate-100 text-slate-700 font-medium transition-colors hover:bg-slate-200 active:bg-slate-300"
            >
              <IconBookOpen className="w-4 h-4" />
              Открыть материалы курса
              <IconExternalLink className="w-3 h-3" />
            </button>
          )}
        </div>
      </div>
    </div>
  );
}

// =============================================================================
// SUCCESS MODAL
// =============================================================================

interface SuccessModalProps {
  score: number;
  maxScore: number;
  percentage: number;
  totalWrongAttempts: number;
  onFinish: () => void;
}

function SuccessModal({
  score,
  maxScore,
  percentage,
  totalWrongAttempts,
  onFinish,
}: SuccessModalProps) {
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50">
      <div className="w-full max-w-sm bg-white rounded-2xl shadow-xl overflow-hidden animate-in zoom-in-95 duration-200">
        <div className="p-6 text-center bg-gradient-to-b from-emerald-50 to-white">
          <div className="w-20 h-20 rounded-full bg-emerald-100 flex items-center justify-center mx-auto mb-4 animate-bounce">
            <IconTrophy className="w-10 h-10 text-emerald-500" />
          </div>
          <h3 className="text-xl font-bold text-slate-900 mb-2">
            Поздравляем!
          </h3>
          <p className="text-sm text-slate-600 mb-4">
            Вы успешно прошли контрольные вопросы
          </p>
          <div className="flex justify-center gap-4">
            <div className="text-center">
              <p className="text-3xl font-bold text-emerald-600">{percentage}%</p>
              <p className="text-xs text-slate-500">Результат</p>
            </div>
            <div className="w-px bg-slate-200" />
            <div className="text-center">
              <p className="text-3xl font-bold text-slate-700">
                {score}/{maxScore}
              </p>
              <p className="text-xs text-slate-500">Правильных</p>
            </div>
          </div>
          {totalWrongAttempts > 0 && (
            <p className="text-xs text-slate-400 mt-3">
              Всего попыток: {totalWrongAttempts + maxScore}
            </p>
          )}
        </div>
        <div className="p-4">
          <button
            type="button"
            onClick={onFinish}
            className="w-full p-3 rounded-xl bg-emerald-600 text-white font-medium transition-colors hover:bg-emerald-700 active:bg-emerald-800"
          >
            Готово
          </button>
        </div>
      </div>
    </div>
  );
}

// =============================================================================
// MAIN COMPONENT
// =============================================================================

export function QuizPlayer({ course, onComplete, onExit }: QuizPlayerProps) {
  const tg = getTelegram();
  const { submitAnswer, isLoading: isSubmitting } = useSubmitAnswer();

  const [currentQuestion, setCurrentQuestion] = useState<ExamQuestionView | null>(
    course.exam?.currentQuestion ?? null
  );
  const [selectedOption, setSelectedOption] = useState<string | null>(null);
  const [answerState, setAnswerState] = useState<AnswerState>("idle");
  const [lastResult, setLastResult] = useState<AnswerResult | null>(null);
  const [showWrongModal, setShowWrongModal] = useState(false);
  const [showSuccessModal, setShowSuccessModal] = useState(false);

  const attemptId = course.exam?.currentAttempt?.id;

  const handleSelectOption = useCallback((optionId: string) => {
    if (answerState !== "idle" || isSubmitting) return;
    setSelectedOption(optionId);
  }, [answerState, isSubmitting]);

  const handleSubmit = useCallback(async () => {
    if (!attemptId || !currentQuestion || !selectedOption || isSubmitting) return;

    setAnswerState("submitting");

    try {
      const result = await submitAnswer({
        attemptId,
        questionId: currentQuestion.id,
        answer: selectedOption,
      });

      setLastResult(result);

      if (result.correct) {
        setAnswerState("correct");

        // Small delay to show correct state, then move to next
        setTimeout(() => {
          if (result.completed) {
            setShowSuccessModal(true);
          } else if (result.nextQuestion) {
            setCurrentQuestion(result.nextQuestion);
            setSelectedOption(null);
            setAnswerState("idle");
          }
        }, 800);
      } else {
        setAnswerState("wrong");
        setTimeout(() => {
          setShowWrongModal(true);
        }, 500);
      }
    } catch (error) {
      setAnswerState("idle");
      console.error("Failed to submit answer:", error);
    }
  }, [attemptId, currentQuestion, selectedOption, isSubmitting, submitAnswer]);

  const handleRetry = useCallback(() => {
    setShowWrongModal(false);
    setSelectedOption(null);
    setAnswerState("idle");
  }, []);

  const handleOpenCourse = useCallback(() => {
    const url = lastResult?.courseUrl ?? course.externalUrl;
    if (!url) return;

    if (isTelegramMiniApp() && tg?.openLink) {
      tg.openLink(url);
    } else {
      window.open(url, "_blank", "noopener,noreferrer");
    }
  }, [lastResult?.courseUrl, course.externalUrl, tg]);

  const handleFinish = useCallback(() => {
    setShowSuccessModal(false);
    onComplete();
  }, [onComplete]);

  if (!currentQuestion || !attemptId) {
    return (
      <div className="p-6 text-center">
        <p className="text-slate-500">Загрузка вопроса...</p>
        <button
          type="button"
          onClick={onExit}
          className="mt-4 text-sm text-blue-600 underline"
        >
          Вернуться назад
        </button>
      </div>
    );
  }

  const progressPercent =
    (currentQuestion.questionNumber / currentQuestion.totalQuestions) * 100;

  return (
    <div className="relative">
      {/* Progress bar */}
      <div className="mb-6">
        <div className="flex items-center justify-between mb-2">
          <span className="text-sm font-medium text-slate-600">
            Вопрос {currentQuestion.questionNumber} из{" "}
            {currentQuestion.totalQuestions}
          </span>
          <span className="text-sm text-slate-400">
            {Math.round(progressPercent)}%
          </span>
        </div>
        <div className="h-2 bg-slate-100 rounded-full overflow-hidden">
          <div
            className="h-full bg-blue-500 rounded-full transition-all duration-500 ease-out"
            style={{ width: `${progressPercent}%` }}
          />
        </div>
      </div>

      {/* Question card */}
      <div className="rounded-2xl overflow-hidden bg-white/95 shadow-sm border border-slate-100 ring-1 ring-slate-200/50 mb-6">
        <div className="p-6">
          <p className="text-lg font-semibold text-slate-900 leading-relaxed">
            {currentQuestion.text}
          </p>
        </div>
      </div>

      {/* Options */}
      <div className="space-y-3 mb-6">
        {currentQuestion.options.map((option) => (
          <OptionButton
            key={option.id}
            optionId={option.id}
            text={option.text}
            selected={selectedOption === option.id}
            disabled={answerState !== "idle" || isSubmitting}
            answerState={answerState}
            selectedOptionId={selectedOption}
            onClick={() => handleSelectOption(option.id)}
          />
        ))}
      </div>

      {/* Submit button */}
      <button
        type="button"
        onClick={handleSubmit}
        disabled={!selectedOption || answerState !== "idle" || isSubmitting}
        className="w-full p-4 rounded-xl bg-blue-600 text-white font-semibold transition-all hover:bg-blue-700 active:bg-blue-800 disabled:bg-slate-300 disabled:cursor-not-allowed"
      >
        {isSubmitting || answerState === "submitting" ? (
          <span className="flex items-center justify-center gap-2">
            <div className="w-5 h-5 border-2 border-white/30 border-t-white rounded-full animate-spin" />
            Проверка...
          </span>
        ) : (
          "Ответить"
        )}
      </button>

      {/* Wrong answer modal */}
      {showWrongModal && lastResult && (
        <WrongAnswerModal
          wrongAttemptCount={lastResult.wrongAttemptCount}
          courseUrl={lastResult.courseUrl}
          onRetry={handleRetry}
          onOpenCourse={handleOpenCourse}
        />
      )}

      {/* Success modal */}
      {showSuccessModal && lastResult && (
        <SuccessModal
          score={lastResult.attempt.score}
          maxScore={lastResult.attempt.maxScore}
          percentage={lastResult.attempt.percentage}
          totalWrongAttempts={lastResult.attempt.totalWrongAttempts}
          onFinish={handleFinish}
        />
      )}
    </div>
  );
}
